import type { AxiosInstance } from 'axios';
import type { ServiceResultT } from '../types/common/ServiceResultT';
import type { ServiceResult } from '../types/common/ServiceResult';
import type { SecondaryCategoryViewModel, SecondaryCategoryCreateModel, SecondaryCategoryUpdateModel } from '../types/secondary-category';
import type { OrderByDescriptor } from '../types/common/OrderByDescriptor';
import { buildPaginationParams, buildOrderByParams } from '../client/queryParams';
import { apiClient } from '../client/client';

export interface SecondaryCategoryQuery {
  name?: string;
  topCategoryId?: number | null;
  isDeleted?: boolean;
  pagination?: { pageSize?: number; pageNumber?: number };
  orderBy?: OrderByDescriptor[] | null;
}

export class SecondaryCategoryService {
  private static readonly BASE = '/api/secondary-category';

  constructor(private readonly client: AxiosInstance = apiClient) {}

  async getAll(query?: SecondaryCategoryQuery): Promise<ServiceResultT<SecondaryCategoryViewModel[]>> {
    const params: Record<string, unknown> = {};
    if (query?.name) params.Name = query.name;
    if (query?.topCategoryId != null) params.TopCategoryId = query.topCategoryId;
    if (query?.isDeleted != null) params.IsDeleted = query.isDeleted;
    Object.assign(params, buildPaginationParams(query?.pagination));
    Object.assign(params, buildOrderByParams(query?.orderBy));
    const { data } = await this.client.get<ServiceResultT<SecondaryCategoryViewModel[]>>(
      SecondaryCategoryService.BASE,
      { params: Object.keys(params).length ? params : undefined }
    );
    return data;
  }

  async getById(id: number | string): Promise<ServiceResultT<SecondaryCategoryViewModel>> {
    const { data } = await this.client.get<ServiceResultT<SecondaryCategoryViewModel>>(
      `${SecondaryCategoryService.BASE}/${id}`
    );
    return data;
  }

  async create(payload: SecondaryCategoryCreateModel): Promise<ServiceResultT<SecondaryCategoryViewModel>> {
    const { data } = await this.client.post<ServiceResultT<SecondaryCategoryViewModel>>(
      SecondaryCategoryService.BASE,
      payload
    );
    return data;
  }

  async update(id: number | string, payload: SecondaryCategoryUpdateModel): Promise<ServiceResultT<SecondaryCategoryViewModel>> {
    const { data } = await this.client.put<ServiceResultT<SecondaryCategoryViewModel>>(
      `${SecondaryCategoryService.BASE}/${id}`,
      payload
    );
    return data;
  }

  async delete(id: number | string): Promise<ServiceResult> {
    const { data } = await this.client.delete<ServiceResult>(`${SecondaryCategoryService.BASE}/${id}`);
    return data;
  }
}

export const secondaryCategoryService = new SecondaryCategoryService();
